import * as React from 'react'

import { Group } from 'konva/lib/Group'
import { Shape, ShapeConfig } from 'konva/lib/Shape'
import { Stage } from 'konva/lib/Stage'
import { TypeIcon } from 'lucide-react'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'
import type {
  ControlPosition,
  DraggableData,
  DraggableEvent,
} from 'react-draggable'
import ReactQuill from 'react-quill-new'

import { IPosition } from '~/client/graph'
import Draggable from '~/client/src/shared/components/LazyDraggable'
import MapViewItemBase from '~/client/src/shared/components/SitemapHelpers/models/MapViewItemBase'
import {
  MAX_RELATIVE_POSITION,
  sitemapMousePointTo,
} from '~/client/src/shared/utils/SitemapCalculationHelpers'

import 'react-quill-new/dist/quill.snow.css'
import './RichTextEditor.scss'

interface IProps {
  canvasSize: ISizes
  selectedMapViewItem: MapViewItemBase
  stage: Stage

  isReferenced?: boolean
}

interface ISizes {
  width: number
  height: number
}
const EDITOR_CLASS = 'ql-editor'

// TODO: redesign and remove react quill as its additional functionality is not used
@observer
export default class RichTextEditor extends React.Component<IProps> {
  private resizableEditor: ReactQuill = null
  // Captured at mount: getEditor() throws once the editor is torn down.
  private editingArea: HTMLElement = null
  @observable private editorSize: ISizes = {
    width: 0,
    height: 0,
  }
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)
    this.localProps = props
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: Readonly<IProps>): void {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }
  }

  private get editorHalfWidth(): number {
    return this.editorSize.width / 2
  }

  private get editorHalfHeight(): number {
    return this.editorSize.height / 2
  }

  @computed
  private get position(): ControlPosition {
    const { canvasSize, selectedMapViewItem, isReferenced } = this.localProps
    const { x, y } =
      selectedMapViewItem.sitemapItemProperties.labelProperties.position

    // scale x, y to canvas and center it
    const offsetX = (this.stage.width() - canvasSize.width) / 2
    const offsetY = (this.stage.height() - canvasSize.height) / 2

    const additionalX = isReferenced
      ? this.itemsLayer.attrs.x
      : offsetX * this.stage.scaleX() + this.stage.x()
    const additionalY = isReferenced
      ? this.itemsLayer.attrs.y
      : offsetY * this.stage.scaleY() + this.stage.y()
    const width = isReferenced
      ? this.imageLayer.attrs.width
      : canvasSize.width * this.stage.scaleX()
    const height = isReferenced
      ? this.imageLayer.attrs.height
      : canvasSize.height * this.stage.scaleY()

    const position: ControlPosition = {
      x:
        (width * x) / MAX_RELATIVE_POSITION +
        additionalX -
        this.editorHalfWidth,
      y:
        (height * y) / MAX_RELATIVE_POSITION +
        additionalY -
        this.editorHalfHeight,
    }

    return position
  }

  public componentDidMount(): void {
    this.resizableEditor?.focus()
  }

  public componentWillUnmount(): void {
    this.resizableEditor = null
    this.editingArea = null
  }

  public render(): JSX.Element {
    const {
      sitemapItemProperties: { labelProperties },
      name,
      color,
    } = this.props.selectedMapViewItem
    const { isTextBoxDisplayed, fontSize, color: labelColor } = labelProperties
    // wrap editor with div to make draggable work
    return (
      <Draggable
        position={this.position}
        onStop={this.dragStop}
        handle=".ql-toolbar.ql-snow"
      >
        <div
          className={classList({
            'text-box': true,
            'show-textbox': isTextBoxDisplayed,
          })}
          style={{ fontSize: fontSize + 'px', color: labelColor }}
        >
          <ReactQuill
            value={name}
            onChange={this.onChange}
            ref={this.setEditor}
          />
          <TypeIcon size={20} color={color} className="move-icon" />
        </div>
      </Draggable>
    )
  }

  private dragStop = (event: DraggableEvent, data: DraggableData): void => {
    const { canvasSize, selectedMapViewItem, isReferenced } = this.props
    const { x, y } =
      selectedMapViewItem.sitemapItemProperties.labelProperties.position

    const width = isReferenced ? this.imageLayer.attrs.width : canvasSize.width
    const height = isReferenced
      ? this.imageLayer.attrs.height
      : canvasSize.height
    let relativePosition: IPosition

    if (isReferenced) {
      const { x: offsetX, y: offsetY } = this.itemsLayer.attrs
      const cursorPosition: IPosition = {
        x: data.x + this.editorHalfWidth - offsetX,
        y: data.y + this.editorHalfHeight - offsetY,
      }

      relativePosition = {
        x: (cursorPosition?.x / width) * MAX_RELATIVE_POSITION - x,
        y: (cursorPosition?.y / height) * MAX_RELATIVE_POSITION - y,
      }
    } else {
      const position: IPosition = {
        x: data.x + this.editorHalfWidth,
        y: data.y + this.editorHalfHeight,
      }
      const mousePointTo: IPosition = sitemapMousePointTo(
        this.stage,
        width,
        height,
        position,
      )

      relativePosition = {
        x: (mousePointTo.x / width) * MAX_RELATIVE_POSITION - x,
        y: (mousePointTo.y / height) * MAX_RELATIVE_POSITION - y,
      }
    }

    selectedMapViewItem.sitemapItemProperties.move(
      relativePosition.x,
      relativePosition.y,
    )
    this.resizableEditor?.focus()
  }

  private setEditor = (ref: ReactQuill) => {
    if (ref) {
      this.resizableEditor = ref
      const editor = ref.getEditor()
      this.editingArea = editor.container as HTMLElement
      // remove default quill class
      // as it adds css styles which hard to override
      editor.root.classList.remove(EDITOR_CLASS)
      const { offsetWidth: width, offsetHeight: height } = this.editingArea
      this.editorSize = { width, height }
    }
  }

  @action.bound
  private onChange(model: string): void {
    this.props.selectedMapViewItem.setName(model)
    this.onResize()
  }

  private onResize = (): void => {
    if (!this.editingArea) {
      return
    }

    const { offsetWidth, offsetHeight } = this.editingArea
    const newEditorSize = { width: offsetWidth, height: offsetHeight }
    const { canvasSize, selectedMapViewItem } = this.props

    const centerDX = (offsetWidth - this.editorSize.width) / 2
    const dx = (centerDX * MAX_RELATIVE_POSITION) / canvasSize.width

    const centerDY = (offsetHeight - this.editorSize.height) / 2
    const dy = (centerDY * MAX_RELATIVE_POSITION) / canvasSize.height
    selectedMapViewItem.sitemapItemProperties.move(dx, dy)
    this.editorSize = newEditorSize
  }

  private get stage(): Stage {
    return this.localProps.stage
  }

  private get itemsLayer(): Group | Shape<ShapeConfig> {
    return this.stage.children[0].children[1]
  }

  private get imageLayer(): Group | Shape<ShapeConfig> {
    return this.stage.children[0].children[0]
  }
}
